import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { 
  FaMapMarkerAlt, 
  FaServer, 
  FaLeaf, 
  FaShieldAlt,
  FaThermometerHalf,
  FaWifi,
  FaArrowRight
} from 'react-icons/fa';
import EditableText from '../components/EditableText';

const features = [
  {
    icon: <FaMapMarkerAlt />,
    title: 'Strategic Location',
    description: 'Located in Kista, Stockholm — Sweden\'s premier technology hub. Low-latency connections and proximity to leading research institutions.'
  },
  {
    icon: <FaServer />,
    title: 'Massive Capacity',
    description: 'Installing over 2,000 NVIDIA GPUs over the next 12 months. Starting with H200, B300/GB300 coming soon.'
  },
  {
    icon: <FaLeaf />,
    title: '100% Renewable Energy',
    description: 'Powered exclusively by renewable energy sources. Reduce your carbon footprint while scaling AI workloads.'
  },
  {
    icon: <FaThermometerHalf />,
    title: 'Heat Recovery',
    description: 'Advanced heat recovery system returns surplus heat directly into Stockholm\'s district heating network.'
  },
  {
    icon: <FaShieldAlt />,
    title: 'Enterprise Security',
    description: 'ISO/IEC 27001:2022 certified facility with round-the-clock security and redundant systems.'
  },
  {
    icon: <FaWifi />,
    title: 'High-Speed Connectivity',
    description: 'Multiple 100G+ network connections with low-latency routing to major European hubs and transatlantic routes.'
  }
];

const stats = [
  { number: '2000+', label: 'NVIDIA GPUs' },
  { number: '100%', label: 'Renewable Energy' },
  { number: '99.99%', label: 'Uptime SLA' },
  { number: '24/7', label: 'Monitoring' }
];

function Infrastructure() {
  const [content, setContent] = useState({});

  useEffect(() => {
    fetch('/api/content?page=infrastructure')
      .then(res => res.json())
      .then(data => setContent(data))
      .catch(() => {});
  }, []);

  return (
    <>
      <Helmet>
        <title>AI Infrastructure | Stockholm Data Center | 6G AI Sweden</title>
        <meta name="description" content="State-of-the-art AI infrastructure in Kista, Stockholm. 2000+ NVIDIA GPUs, sustainable data center with renewable energy and heat recovery." />
        <meta name="keywords" content="AI infrastructure, data center, Stockholm, Kista, atNorth, sustainable AI, NVIDIA GPUs" />
        <meta property="og:title" content="AI Infrastructure | 6G AI Sweden" />
        <meta property="og:description" content="State-of-the-art AI infrastructure in Stockholm, Sweden." />
      </Helmet>

      {/* Page Header */}
      <section className="page-header">
        <Container>
          <Row>
            <Col lg={8}>
              <h1 className="page-title"><EditableText section="page_title" page="infrastructure" defaultValue="Infrastructure" as="span" /></h1>
              <p className="page-subtitle">
                <EditableText section="page_subtitle" page="infrastructure" defaultValue="World-class AI infrastructure powered by sustainable energy in the heart of Stockholm." as="span" />
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Stats */}
      <section className="section section-dark">
        <Container>
          <div className="stats-container">
            {stats.map((stat, index) => (
              <div key={index} className="stat-box">
                <div className="stat-box-number">
                  <EditableText section={`stat_${index}_number`} page="infrastructure" defaultValue={stat.number} as="span" />
                </div>
                <div className="stat-box-label">
                  <EditableText section={`stat_${index}_label`} page="infrastructure" defaultValue={stat.label} as="span" />
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Datacenter Info */}
      <section className="section">
        <Container>
          <Row className="align-items-center mb-5">
            <Col lg={6}>
              <h2 className="section-title"><EditableText section="datacenter_title" page="infrastructure" defaultValue="atNorth Data Center, Kista" as="span" /></h2>
              <p className="section-subtitle text-start">
                <EditableText section="datacenter_desc" page="infrastructure" defaultValue="Our facility is hosted at atNorth's state-of-the-art data center in Kista, Stockholm's technology district. This prime location offers:" as="span" />
              </p>
              <ul className="service-features" style={{ fontSize: '1.1rem' }}>
                <li><EditableText section="datacenter_list_1" page="infrastructure" defaultValue="Strategic proximity to Sweden's leading tech companies" as="span" /></li>
                <li><EditableText section="datacenter_list_2" page="infrastructure" defaultValue="Access to world-class research institutions" as="span" /></li>
                <li><EditableText section="datacenter_list_3" page="infrastructure" defaultValue="Excellent network infrastructure" as="span" /></li>
                <li><EditableText section="datacenter_list_4" page="infrastructure" defaultValue="Skilled workforce in the area" as="span" /></li>
                <li><EditableText section="datacenter_list_5" page="infrastructure" defaultValue="Stable Nordic climate for natural cooling" as="span" /></li>
              </ul>
            </Col>
            <Col lg={6}>
              <div style={{ 
                background: 'var(--md-sys-color-surface-container)', 
                borderRadius: 'var(--radius-large)', 
                padding: '2rem',
                border: '1px solid var(--md-sys-color-outline-variant)'
              }}>
                <h4 style={{ marginBottom: '1.5rem' }}>Facility Specifications</h4>
                <div className="infra-feature">
                  <div className="infra-icon"><FaServer /></div>
                  <div className="infra-content">
                    <h4><EditableText section="spec_power_title" page="infrastructure" defaultValue="Power Capacity" as="span" /></h4>
                    <p><EditableText section="spec_power_desc" page="infrastructure" defaultValue="Multi-megawatt capacity with redundant power feeds" as="span" /></p>
                  </div>
                </div>
                <div className="infra-feature">
                  <div className="infra-icon"><FaShieldAlt /></div>
                  <div className="infra-content">
                    <h4><EditableText section="spec_security_title" page="infrastructure" defaultValue="Security" as="span" /></h4>
                    <p><EditableText section="spec_security_desc" page="infrastructure" defaultValue="24/7 security, CCTV monitoring" as="span" /></p>
                  </div>
                </div>
                <div className="infra-feature">
                  <div className="infra-icon"><FaLeaf /></div>
                  <div className="infra-content">
                    <h4><EditableText section="spec_cooling_title" page="infrastructure" defaultValue="Cooling" as="span" /></h4>
                    <p><EditableText section="spec_cooling_desc" page="infrastructure" defaultValue="Advanced cooling systems with free cooling capability" as="span" /></p>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Features Grid */}
      <section className="section section-dark">
        <Container>
          <div className="text-center mb-5">
            <h2 className="section-title"><EditableText section="highlights_title" page="infrastructure" defaultValue="Infrastructure Highlights" as="span" /></h2>
            <p className="section-subtitle"><EditableText section="highlights_subtitle" page="infrastructure" defaultValue="Built for enterprise AI workloads" as="span" /></p>
          </div>
          <Row>
            {features.map((feature, index) => (
              <Col md={6} lg={4} key={index} className="mb-4">
                <div className="value-card">
                  <div className="value-icon">{feature.icon}</div>
                  <h4 className="value-title"><EditableText section={`feature_${index}_title`} page="infrastructure" defaultValue={feature.title} as="span" /></h4>
                  <p className="value-text"><EditableText section={`feature_${index}_desc`} page="infrastructure" defaultValue={feature.description} as="span" /></p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Sustainability */}
      <section className="section">
        <Container>
          <Row className="align-items-start">
            <Col lg={6}>
              <div className="info-card-primary">
                <h2 className="info-card-title">
                  <FaLeaf className="info-card-icon" />
                  <EditableText section="sustainable_title" page="infrastructure" defaultValue="Sustainable Computing" as="span" />
                </h2>
                <p className="info-card-text">
                  <EditableText section="sustainable_desc" page="infrastructure" defaultValue="Environmental sustainability is a core pillar of 6G AI Sweden's strategy. Our infrastructure is designed to minimize environmental impact while maximizing performance." as="span" />
                </p>
                <ul className="service-features">
                  <li><EditableText section="sustainable_list_1" page="infrastructure" defaultValue="100% renewable energy from Nordic hydro and wind" as="span" /></li>
                  <li><EditableText section="sustainable_list_2" page="infrastructure" defaultValue="Heat recovery system feeding Stockholm district heating" as="span" /></li>
                  <li><EditableText section="sustainable_list_3" page="infrastructure" defaultValue="Energy-efficient cooling systems" as="span" /></li>
                </ul>
              </div>
            </Col>
            <Col lg={6}>
              <div className="info-card">
                <h2 className="section-title text-start"><EditableText section="roadmap_title" page="infrastructure" defaultValue="GPU Capacity Roadmap" as="span" /></h2>
                <p className="section-subtitle text-start">
                  <EditableText section="roadmap_desc" page="infrastructure" defaultValue="We're rapidly expanding our GPU infrastructure to meet growing demand." as="span" />
                </p>
                <div className="roadmap-list">
                  <div className="infra-feature">
                    <div className="infra-icon"><FaServer /></div>
                    <div className="infra-content">
                      <h4>Q3 2025</h4>
                      <p>Initial H200 cluster deployment live</p>
                    </div>
                  </div>
                  <div className="infra-feature">
                    <div className="infra-icon"><FaServer /></div>
                    <div className="infra-content">
                      <h4>Q2-Q3 2026</h4>
                      <p>B300/GB300 Blackwell GPUs coming soon</p>
                    </div>
                  </div>
                  <div className="infra-feature">
                    <div className="infra-icon"><FaServer /></div>
                    <div className="infra-content">
                      <h4>Q4 2026</h4>
                      <p>Full 2000+ GPU capacity achieved</p>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA */}
      <section className="section section-dark">
        <Container>
          <div className="cta-section">
            <h2 className="cta-title">Ready to Deploy?</h2>
            <p className="cta-text">
              Get in touch to discuss your infrastructure requirements.
            </p>
            <Button variant="primary" size="lg" as={Link} to="/contact">
              Contact Sales <FaArrowRight />
            </Button>
          </div>
        </Container>
      </section>
    </>
  );
}

export default Infrastructure;
